import { useAuth } from '@AuthProvider';
import { Box, Step, StepConnector, stepConnectorClasses, Stepper, StepLabel, MobileStepper } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useApp } from 'src/contexts/ApplicationContext';
import { useLoader } from 'src/contexts/LoaderContext';
import { useResponsive } from 'src/hooks/use-responsive';

import { steps } from '../steps/steps';

const LilacConnector = styled(StepConnector)(({ theme }) => ({
  [`& .${stepConnectorClasses.line}`]: {
    borderTopWidth: 2,
    borderColor: theme.palette.grey[300],
  },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.grey[500],
  },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.grey[500],
  },
}));

export default function OnboardingSpartacusV2Stepper({
  handleCompleteOnboarding,
}: {
  handleCompleteOnboarding: () => void;
}) {
  const { currentOrganization } = useApp();
  const { isLoading } = useAuth();
  const { hideLoader } = useLoader();
  const [activeStep, setActiveStep] = useState(0);

  const isMobile = useResponsive('down', 'sm');

  useEffect(() => {
    if (!isLoading) hideLoader();
  }, [isLoading]);

  // Update step when organization is loaded
  useEffect(() => {
    if (currentOrganization.id !== -1 && activeStep === 0) {
      setActiveStep(1);
    }
  }, [currentOrganization.id, activeStep]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleComplete = () => {
    handleCompleteOnboarding();
  };

  return (
    <Box
      p={3}
      sx={{ width: '100%', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
    >
      {isMobile ? (
        <MobileStepper
          variant="dots"
          steps={steps.length}
          position="static"
          activeStep={activeStep}
          sx={{
            maxWidth: 400,
            '& .MuiMobileStepper-dot': { backgroundColor: 'action.disabledBackground' },
            '& .MuiMobileStepper-dotActive': { backgroundColor: 'secondary.darker' },
          }}
          backButton={null}
          nextButton={null}
        />
      ) : (
        <Stepper activeStep={activeStep} connector={<LilacConnector />} sx={{ width: '100%', maxWidth: 560 }}>
          {steps.map((step, index) => (
            <Step key={index}>
              <StepLabel
                slotProps={{
                  stepIcon: {
                    sx: {
                      '&.Mui-active': { color: 'secondary.darker' },
                      '&.Mui-completed': { color: 'secondary.darker' },
                    },
                  },
                }}
              >
                {activeStep === index ? step.name : ''}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      )}
      {(() => {
        const StepComponent = steps[activeStep]?.component;
        if (activeStep === steps.length - 1) {
          return StepComponent ? <StepComponent onNext={handleComplete} /> : null;
        }
        return StepComponent ? <StepComponent onNext={handleNext} /> : null;
      })()}
    </Box>
  );
}
